import * as React from 'react'
import { Form, Input, Spin, Button, Radio, Tooltip, TimePicker } from 'antd'
import { debounce, round, set, get, cloneDeep } from 'lodash'
import produce from 'immer'
import moment from 'moment'
import { showError, resourceNotFound, ValidationError } from 'helpers/errors'
import { tryParseMoment, ZERO_HOUR } from 'helpers/dateTime'
import {
  createLabelFactory,
  createFieldFactory,
  createHandleSubmit,
  createDropdownRender,
  isReadOnly,
} from 'helpers/formViews'
import { formatValue } from 'helpers/listViews'
import labourTypes from 'options/jobLabourTypes'
import { tryParseInt, DEBOUNCE } from 'helpers/utils'
import { t, toLocaleCurrency } from 'helpers/i18n'
import { createFilterUserSuppliers } from 'helpers/auth'
import { stopEvent } from 'helpers/events'
import { getStorageItem, setStorageItem } from 'helpers/localStorage'
import Select, { Option, getOptionProps } from 'elements/Select'
import InputNumber from 'elements/InputNumber'
import DatePicker from 'elements/DatePicker'
import { Row, Col } from 'elements/Grid'

const PADDING_TOP = { paddingTop: '28px' }

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
    this.searchOperators = debounce(this.searchOperators, DEBOUNCE)
    this.searchSuppliers = debounce(this.searchSuppliers, DEBOUNCE)
  }

  transformSaveItemParams = ({ item, values }) => {
    const { startTime, stopTime, timeType, actualHours, labourRateId, labourDate } = { ...item, ...values }
    const labourRate = this.state.jobLabourRates.find((one) => one.id === labourRateId)
    const startMoment = tryParseMoment(startTime, ZERO_HOUR).year(2000).month(1).date(1)
    const stopMoment = tryParseMoment(stopTime, ZERO_HOUR).year(2000).month(1).date(1)
    const hours =
      timeType === 'StartStop'
        ? round(Math.abs(startMoment.diff(stopMoment, 'minutes')) / 60.0, 2)
        : Math.max(actualHours, 0)

    return {
      startTime: startMoment.clone().toJSON(),
      stopTime: stopMoment.clone().toJSON(),
      actualHours: hours,
      timeSpan:
        timeType === 'StartStop'
          ? `${startMoment.format('h:mm A')} To ${stopMoment.format('h:mm A')}`
          : t('na'),
      labourRateDescription: labourRate
        ? `${labourRate.description} (${toLocaleCurrency(labourRate?.rate)}/hr)`
        : '',
      totalCost: hours * (labourRate?.rate ?? 0),
      labourDate: moment(labourDate).startOf('day').add(12, 'hours').toJSON(),
    }
  }

  componentDidMount() {
    this.fetchItem()
  }

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.id].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({
                timeType: 'Duration',
                labourType: 'Operator',
                actualHours: 0,
                startTime: ZERO_HOUR,
                stopTime: ZERO_HOUR,
                labourDate: moment().startOf('day').add(12, 'hours').toJSON(),
                totalCost: 0,
              })
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
      ])

      const item = cloneDeep(responses[0].value.data)

      item.startTime = tryParseMoment(item.startTime || ZERO_HOUR)
      item.stopTime = tryParseMoment(item.stopTime || ZERO_HOUR)

      const [jobLabourRates, operators, suppliers] = await Promise.all([
        this.props.getJobLabourRates({ jobLabourRateIds: item.labourRateId ? [item.labourRateId] : [] }),
        this.props.getOperators({
          operatorIds:
            item.labourType === 'Operator' && item.performedById ? [item.performedById] : undefined,
        }),
        this.props.getSuppliers({
          supplierIds:
            item.labourType === 'Supplier' && item.performedById ? [item.performedById] : undefined,
        }),
      ])

      if (!item.id && !item.labourRateId) {
        item.labourRateId =
          tryParseInt(getStorageItem('jobs.formView.labour.formView.labourRateId')) ||
          get(jobLabourRates, 'value.data.items[0].id')
      }

      this.setState({
        item,
        fieldSettings: responses[1].value.data.fieldSettings,
        jobLabourRates: jobLabourRates.value.data.items,
        operators: operators?.value?.data ?? {},
        suppliers: suppliers?.value?.data ?? {},
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        if (name === 'labourRateId') {
          setStorageItem('jobs.formView.labour.formView.labourRateId', value)
        }

        if (name === 'labourType') {
          draft.item.performedById = null
          draft.item.performedByName = ''
        }

        if (name === 'performedById') {
          if (draft.item.labourType === 'Operator' && draft.operators?.items) {
            draft.item.performedByName = draft.operators.items.find((one) => one.id === value).displayName
          }

          if (draft.item.labourType === 'Supplier' && draft.suppliers?.items) {
            draft.item.performedByName = draft.suppliers.items.find((one) => one.id === value).displayName
          }
        }

        if (name === 'actualHours') {
          draft.item.timeSpan = 'N/A'
          draft.item.startTime = ZERO_HOUR
          draft.item.stopTime = ZERO_HOUR
        }

        if (name === 'timeType' && value === 'Duration') {
          draft.item.startTime = ZERO_HOUR
          draft.item.stopTime = ZERO_HOUR
        }

        if (name === 'startTime' || name === 'stopTime') {
          const startMoment = tryParseMoment(draft.item.startTime, ZERO_HOUR).year(2000).month(1).date(1)
          const stopMoment = tryParseMoment(draft.item.stopTime, ZERO_HOUR).year(2000).month(1).date(1)

          draft.item.actualHours = round(Math.abs(startMoment.diff(stopMoment, 'minutes')) / 60.0, 2)
        }

        const labourRate = draft.jobLabourRates.find((one) => one.id === draft.item.labourRateId)
        draft.item.totalCost = draft.item.actualHours * (labourRate?.rate ?? 0)

        const { labourRateId, labourType, performedById, labourDate, startTime, stopTime, actualHours } =
          draft.item

        this.props.form.setFieldsValue({
          labourRateId,
          labourType,
          performedById,
          labourDate,
          startTime,
          stopTime,
          actualHours,
        })
      })
    )

  searchOperators = async (search) => {
    try {
      const { item } = this.state
      const response = await this.props.getOperators({
        search,
        operatorIds: item.performedById ? [item.performedById] : undefined,
      })

      this.setState({ operators: response.value.data })
    } catch (error) {
      showError({ error })
    }
  }

  searchSuppliers = async (search) => {
    try {
      const { item } = this.state
      const response = await this.props.getSuppliers({
        search,
        supplierIds: item.performedById ? [item.performedById] : undefined,
      })

      this.setState({ suppliers: response.value.data })
    } catch (error) {
      showError({ error })
    }
  }

  render() {
    const { item, fieldSettings, operators, suppliers } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)
    const readOnly = isReadOnly(this)

    return (
      <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
        <div className="form-items-container">
          <ValidationError errors={this.state.validationErrors} />
          <Form.Item label={createLabel('labourRateId')}>
            {createFieldDecorator('labourRateId')(
              <Select onChange={(value) => this.setItemValue('labourRateId', value)} allowClear={false}>
                {(this.state.jobLabourRates ?? []).map((each) => (
                  <Option key={each.id} value={each.id}>
                    <span {...getOptionProps(each)}>{each.displayName}</span>
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
          <Form.Item label={createLabel('labourType')}>
            {createFieldDecorator('labourType')(
              <Select onChange={(value) => this.setItemValue('labourType', value)} allowClear={false}>
                {Object.entries(labourTypes).map(([key, value]) => (
                  <Option key={key} value={key}>
                    {t(value)}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
          {item.labourType === 'Operator' && (
            <Form.Item label={createLabel('performedById')}>
              {createFieldDecorator('performedById')(
                <Select
                  onChange={(value) => this.setItemValue('performedById', value)}
                  dropdownRender={createDropdownRender(operators)}
                  onSearch={this.searchOperators}
                  allowClear={false}
                  showSearch
                >
                  {(operators?.items ?? []).map((each) => (
                    <Option key={each.id} value={each.id}>
                      <span {...getOptionProps(each)}>{each.displayName}</span>
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          )}
          {item.labourType === 'Supplier' && (
            <Form.Item label={createLabel('performedById')}>
              {createFieldDecorator('performedById')(
                <Select
                  onChange={(value) => this.setItemValue('performedById', value)}
                  dropdownRender={createDropdownRender(suppliers)}
                  onSearch={this.searchSuppliers}
                  allowClear={false}
                  showSearch
                >
                  {(suppliers?.items ?? [])
                    .filter(createFilterUserSuppliers(item.performedById))
                    .map((each) => (
                      <Option key={each.id} value={each.id}>
                        <span {...getOptionProps(each)}>{each.displayName}</span>
                      </Option>
                    ))}
                </Select>
              )}
            </Form.Item>
          )}
          <Form.Item label={createLabel('labourDate')}>
            {createFieldDecorator('labourDate')(
              <DatePicker onChange={(value) => this.setItemValue('labourDate', value)} allowClear={false} />
            )}
          </Form.Item>
          <Row gutter={24}>
            <Col xs={2} style={PADDING_TOP}>
              <Radio
                checked={item.timeType === 'StartStop'}
                onChange={(e) => this.setItemValue('timeType', 'StartStop')}
              />
            </Col>
            <Col xs={11}>
              <Form.Item label={createLabel('startTime')}>
                {createFieldDecorator('startTime')(
                  <TimePicker
                    className="w-full"
                    onChange={(value) => this.setItemValue('startTime', value)}
                    allowClear={false}
                    disabled={item.timeType !== 'StartStop'}
                    format="h:mm A"
                    use12Hours
                  />
                )}
              </Form.Item>
            </Col>
            <Col xs={11}>
              <Form.Item label={createLabel('stopTime')}>
                {createFieldDecorator('stopTime')(
                  <TimePicker
                    className="w-full"
                    onChange={(value) => this.setItemValue('stopTime', value)}
                    allowClear={false}
                    disabled={item.timeType !== 'StartStop'}
                    format="h:mm A"
                    use12Hours
                  />
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={2} style={PADDING_TOP}>
              <Radio
                checked={item.timeType === 'Duration'}
                onChange={(e) => this.setItemValue('timeType', 'Duration')}
              />
            </Col>
            <Col xs={11}>
              <Form.Item label={createLabel('actualHours')}>
                {createFieldDecorator('actualHours')(
                  <InputNumber
                    onChange={(value) => this.setItemValue('actualHours', value)}
                    disabled={item.timeType !== 'Duration'}
                    step={0.5}
                  />
                )}
              </Form.Item>
            </Col>
            <Col xs={11} style={PADDING_TOP}>
              {item.labourRateDescription}
            </Col>
          </Row>
          <Form.Item label={createLabel('totalCost')}>
            <Input
              value={formatValue({
                value: item.totalCost,
                displayFormat: fieldSettings.find((one) => one.dtoFieldName === 'totalCost').displayFormat,
              })}
              disabled
            />
          </Form.Item>
        </div>
        <div className="form-buttons-container">
          <Button onClick={this.props.onCancel}>{t(readOnly ? 'close' : 'cancel')}</Button>{' '}
          {!readOnly && (
            <Tooltip title={t('childRecordSaveInfo')} placement="topLeft">
              <Button type="primary" htmlType="submit" loading={this.state.saveAndCloseButtonLoading}>
                {!this.props.linkTargetRecord ? t('add') : t('update')}
              </Button>
            </Tooltip>
          )}
        </div>
      </Form>
    )
  }
}

export default Form.create()(FormView)
